/**
 * Single source of truth for how we derive a human-visible property label from persisted fields.
 * Call sites receive rows after Prisma + org-at-rest decrypt; do not decrypt here.
 */

export type PropertyDisplaySource = {
  name: string;
  contents?: { title?: string | null; language?: string }[] | null;
  channelAccommodationName?: string | null;
  channelRawData?: unknown;
};

function listingNameFromChannelRawData(channelRawData: unknown): string | null {
  if (channelRawData == null || typeof channelRawData !== 'object') return null;
  const r = channelRawData as Record<string, unknown>;
  const top = r.name;
  if (typeof top === 'string' && top.trim()) return top.trim();
  const rooms = r.rooms;
  if (Array.isArray(rooms) && rooms[0] && typeof rooms[0] === 'object' && rooms[0] !== null) {
    const rn = (rooms[0] as Record<string, unknown>).name;
    if (typeof rn === 'string' && rn.trim()) return rn.trim();
  }
  const roomTypes = r.roomTypes;
  if (Array.isArray(roomTypes) && roomTypes[0] && typeof roomTypes[0] === 'object' && roomTypes[0] !== null) {
    const rn = (roomTypes[0] as Record<string, unknown>).name;
    if (typeof rn === 'string' && rn.trim()) return rn.trim();
  }
  return null;
}

/**
 * Canonical label for one property row: trimmed internal `name`, else first content title,
 * else channel listing name, else PMS JSON mirror. Same ordering everywhere.
 */
export function resolvePropertyLabel(src: PropertyDisplaySource): string {
  const trimmed = typeof src.name === 'string' ? src.name.trim() : '';
  if (trimmed) return trimmed;
  const fromContent = src.contents?.map((c) => c.title?.trim()).find(Boolean);
  if (fromContent) return fromContent;
  const ch = src.channelAccommodationName?.trim();
  if (ch) return ch.slice(0, 50);
  const pms = listingNameFromChannelRawData(src.channelRawData);
  if (pms) return pms.slice(0, 200);
  return '';
}

/**
 * When `row` is a subunit (has a parent property), combine labels. If the subunit has no
 * own label, show the parent label only (anonymous subunit).
 */
export function resolveLabelWithParent(
  row: PropertyDisplaySource,
  parent: PropertyDisplaySource | null | undefined,
): string {
  const self = resolvePropertyLabel(row);
  if (!parent) return self;
  const p = resolvePropertyLabel(parent);
  if (self.trim()) {
    if (!p.trim()) return self;
    return `${p} - ${self}`;
  }
  return p;
}

/** Legacy string-only helper; prefer {@link resolveLabelWithParent} when full rows are available. */
export function getPropertyDisplayNameWithParent(
  displayName: string,
  parentDisplayName?: string | null,
): string {
  const c = displayName?.trim() ?? '';
  const par = parentDisplayName?.trim() ?? '';
  if (!par) return c;
  if (!c) return par;
  return `${par} - ${c}`;
}
